import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, X, Folder, CheckCircle2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { TaskCard } from '../components/tasks/TaskCard';
import { useTaskStore } from '../stores/taskStore';
import { useProjectStore } from '../stores/projectStore';
import type { TaskFilters } from '../stores/taskStore';

/**
 * User Tasks Page - Admin-only view
 * Shows tasks created by other users that are NOT assigned to the current admin.
 * Group tasks where admin is a project participant appear in normal views (Inbox/Projects),
 * NOT in this User Tasks view.
 * Completed tasks (within 24h) are shown in a separate section at the bottom,
 * following the same pattern as InboxPage/TodayPage.
 */
export const UserTasksPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const tasks = useTaskStore((state) => state.tasks);
  const fetchTasks = useTaskStore((state) => state.fetchTasks);
  const setFilters = useTaskStore((state) => state.setFilters);
  const isLoading = useTaskStore((state) => state.isLoading);
  const filters = useTaskStore((state) => state.filters);
  const clearFilters = useTaskStore((state) => state.clearFilters);
  const projects = useProjectStore((state) => state.projects);
  const fetchProjects = useProjectStore((state) => state.fetchProjects);

  const initialFetchDone = useRef(false);
  const lastPath = useRef(location.pathname);

  // Fetch projects for filter display
  useEffect(() => {
    if (filters.projectId) {
      fetchProjects();
    }
  }, [filters.projectId, fetchProjects]);

  // Fetch user tasks - tasks created by others, not assigned to current user
  useEffect(() => {
    if (lastPath.current === location.pathname && initialFetchDone.current) {
      return;
    }

    lastPath.current = location.pathname;
    initialFetchDone.current = true;

    const fetchParams: Record<string, unknown> = { view: 'user-tasks' };
    if (filters.projectId) {
      fetchParams.projectId = filters.projectId;
    }
    if (filters.search) {
      fetchParams.search = filters.search;
    }
    setFilters(fetchParams as TaskFilters);
    fetchTasks(fetchParams);
  }, [location.pathname]);

  // Find the current project for display
  const currentProject = useMemo(() => {
    if (!filters.projectId) return null;
    return projects.find(p => p.id === filters.projectId);
  }, [filters.projectId, projects]);

  // Clear project filter
  const handleClearProjectFilter = () => {
    clearFilters();
    fetchTasks({ view: 'user-tasks' });
  };

  // Separate tasks into pending and completed (within 24h)
  const { pendingTasks, completedTasks } = useMemo(() => {
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    const pending = tasks.filter(task => !task.completed);
    const completed = tasks.filter(task => {
      if (!task.completed) return false;
      if (!task.completedAt) return false;
      return new Date(task.completedAt).getTime() > twentyFourHoursAgo;
    });
    return { pendingTasks: pending, completedTasks: completed };
  }, [tasks]);

  const completedCount = completedTasks.length;

  // Group pending tasks by creator (only pending, not completed)
  const tasksByCreator = useMemo(() => {
    const grouped: Record<string, typeof pendingTasks> = {};
    pendingTasks.forEach(task => {
      const creatorName = task.creator 
        ? `${task.creator.firstName} ${task.creator.lastName}`
        : t('tasks.ui.unknownUser');
      if (!grouped[creatorName]) {
        grouped[creatorName] = [];
      }
      grouped[creatorName].push(task);
    });
    return grouped;
  }, [pendingTasks, t]);

  const creatorNames = Object.keys(tasksByCreator).sort();
  const hasTasks = pendingTasks.length > 0 || completedCount > 0;

  // Count pending tasks per creator (only pending, not misleading total)
  const totalTasksByCreator = useMemo(() => {
    const grouped: Record<string, number> = {};
    pendingTasks.forEach(task => {
      const creatorName = task.creator 
        ? `${task.creator.firstName} ${task.creator.lastName}`
        : t('tasks.ui.unknownUser');
      grouped[creatorName] = (grouped[creatorName] || 0) + 1;
    });
    return grouped;
  }, [pendingTasks, t]);

  return (
    <div className="flex flex-col h-full">
      <Header title={t('userTasks.title')} showSearch />

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Project Filter Badge */}
        {currentProject && (
          <div className="mb-4">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity"
              style={{ backgroundColor: `${currentProject.color}20`, color: currentProject.color }}
            >
              <Folder className="w-4 h-4" />
              <span>{currentProject.name}</span>
              <button
                onClick={handleClearProjectFilter}
                className="ml-1 p-0.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                {t('userTasks.infoTitle')}
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                {t('userTasks.infoDescription')}
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">{t('common.loading')}</div>
          </div>
        ) : !hasTasks ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-4xl mb-4">👥</div>
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              {t('userTasks.emptyTitle')}
            </div>
            <p className="text-sm text-gray-500 max-w-md">
              {t('userTasks.emptyDescription')}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Completed Badge */}
            {completedCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg w-fit">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  {t('userTasks.completedLast24h', { count: completedCount })}
                </span>
              </div>
            )}

            {/* Pending Tasks Section - grouped by creator */}
            {pendingTasks.length > 0 && (
              <div className="space-y-8">
                {creatorNames.map(creatorName => (
                  <div key={creatorName} className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-xs text-primary-600 dark:text-primary-400">
                        {creatorName.split(' ').map(n => n[0]).join('')}
                      </span>
                      {creatorName}
                      <span className="text-xs normal-case text-gray-400">
                        {t('userTasks.taskCount', { count: totalTasksByCreator[creatorName] })}
                      </span>
                    </h3>
                    <div className="space-y-3">
                      {tasksByCreator[creatorName].map((task) => (
                        <TaskCard key={task.id} task={task} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Completed Tasks Section (within 24h) */}
            {completedTasks.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('userTasks.completedSection')}
                </h3>
                {completedTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default UserTasksPage;
